import { Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import { JwtHelperService } from '@auth0/angular-jwt';
import { Observable } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';

/* State and Services */
import { ApiService, LoginResponse, SetAuthResponse } from './api.service';

/* Actions */
import { ResetAppData, SetData } from '@core/store/app/app.action';
import { ResetDashboardData } from '@core/store/dashboard/dashboard.actions';
import { ResetPortfolioData } from '@core/store/portfolio/portfolio.actions';
import { Logout } from '@core/store/auth/auth.action';

/* Models */
import { AuthObj, User } from '../models/user.model';
import { Customer } from '../models/customer.model';

const helper = new JwtHelperService();

@Injectable({
	providedIn: 'root',
})
export class AuthService {
	private tokenExpirationTimer: any;
	private loginAt: number;
	private maxAge: number;

	constructor(private apiService: ApiService, private store: Store) {}

	login(authObj: AuthObj): Observable<LoginResponse>
	{
		this.loginAt = Date.now();
		return this.apiService.authenticateUser(authObj).pipe(
			map((response) => {
				if (response.status === 206) {
					throw response.body.message;
				}

				// dispatch action to store app data
				this.store.dispatch(new SetData(response.body.data));

				// get expiration date and set expiration timer
				this.maxAge = response.body.maxAge;
				this.autoLogout(this.maxAge);

				return response.body;
			}),
		);
	}

	refresh()
	{
		if (this.loginAt + this.maxAge * 0.5 < Date.now() && this.tokenExpirationTimer)
			this.apiService.refreshSession().then(remaining =>
			{
				this.loginAt = Date.now();
				clearTimeout(this.tokenExpirationTimer);
				this.autoLogout(remaining);
			});
	}

	logout(): Observable<object> {
		return this.apiService.logoutUser().pipe(
			tap(() => {
				this.store.dispatch([new ResetAppData(), new ResetDashboardData(), new ResetPortfolioData()]);

				// clear and reset token expiration timer
				if (this.tokenExpirationTimer) {
					clearTimeout(this.tokenExpirationTimer);
				}
				this.tokenExpirationTimer = null;
			}),
		);
	}

	autoLogout(expiresIn: number): void {
		this.tokenExpirationTimer = setTimeout(() => this.store.dispatch(new Logout()), expiresIn);
	}

	loginWithRecoveryCode(
		recoveryCode: string,
		loginCredentials: { username: string; password: string },
	): Observable<LoginResponse> {
		return this.apiService
			.setTwoFactorAuth('validate-recovery-code', {
				recoveryCode,
				username: loginCredentials.username,
			})
			.pipe(
				map((isValid: boolean) => {
					if (!isValid) {
						throw new Error('Invalid recovery code');
					}

					return isValid;
				}),
				switchMap(() => this.login({ ...loginCredentials, recoveryCode: true })),
			);
	}

	setupAuth(username: string): Observable<SetAuthResponse> {
		return this.apiService
			.setTwoFactorAuth('setup', { username })
			.pipe(map((authResponse: SetAuthResponse) => authResponse));
	}

	verifyAuth(username: string, tempSecret: string, authCode: string): Observable<boolean> {
		return this.apiService.setTwoFactorAuth('verify', { username, tempSecret, authCode }).pipe(
			map((isVerified: boolean) => {
				if (!isVerified) {
					throw new Error('Invalid verification code');
				}

				return isVerified;
			}),
		);
	}

	deactivateAuth(username: string): Observable<boolean> {
		return this.apiService.setTwoFactorAuth('deactivate', { username }).pipe(
			map((isDeactivated: boolean) => {
				if (!isDeactivated) {
					throw new Error('Deactivation failed');
				}

				return isDeactivated;
			}),
		);
	}

	generateRecoveryCodes(userId: number): Observable<string[]> {
		return this.apiService.setTwoFactorAuth('generate-recovery-codes', { userId }).pipe(
			map((recoveryCodes: string[]) => {
				if (!recoveryCodes) {
					throw new Error('Failed to generate recovery codes');
				}

				return recoveryCodes;
			}),
		);
	}

	updateUserAccount({ field, value, dataType }): Observable<User | Customer> {
		return this.apiService.updateUserAccount({ [field]: value, dataType }).pipe(
			map((data) => {
				if (dataType === 'customer') {
					return data as Customer;
				}

				return {
					avatar: data.avatar,
					customerId: data.customer,
					email: data.email,
					id: data.id,
					name: data.name,
					settings: data.settings,
					username: data.username,
				} as User;
			}),
		);
	}

	requestPasswordChange(email: string): Observable<boolean> {
		return this.apiService.resetPassword({ email }).pipe(
			tap((response) => {
				if (!response) {
					throw new Error('The specified email address could not be found.');
				}
			}),
		);
	}

	resetPassword(password: string, token: string): Observable<boolean> {
		return this.apiService.resetPassword({ password, token }).pipe(
			tap((response) => {
				if (!response) {
					throw new Error('Invalid token.');
				}
			}),
		);
	}

	validateToken(token: string): Observable<string> {
		return this.apiService.resetPassword({ token }).pipe(map((response) => (response ? token : null)));
	}
}
